<template>
  <v-dialog v-model="dialogVisible" max-width="1150" @click:outside="closeDialog">
    <v-card style="border-radius: 12px;">
      <v-card-title style="background: #4a5568; color: white; border-radius: 12px 12px 0 0;">
        <span class="dialog-title">Bin Status Overview</span>
        <v-spacer></v-spacer>
        <v-btn icon @click="closeDialog" style="color: white;">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-4">
        <!-- Summary Stats -->
        <v-row class="mb-4">
          <v-col cols="12" md="4">
            <div class="stat-card available-stat">
              <div class="stat-icon">
                <v-icon color="white">mdi-checkbox-marked-circle</v-icon>
              </div>
              <div class="stat-content">
                <div class="stat-value">{{ availableBins.length }}</div>
                <div class="stat-label">Available Bins</div>
              </div>
            </div>
          </v-col>
          <v-col cols="12" md="4">
            <div class="stat-card taken-stat">
              <div class="stat-icon">
                <v-icon color="white">mdi-close-circle</v-icon>
              </div>
              <div class="stat-content">
                <div class="stat-value">{{ takenBins.length }}</div>
                <div class="stat-label">Taken Bins</div>
              </div>
            </div>
          </v-col>
          <v-col cols="12" md="4">
            <div class="stat-card total-stat">
              <div class="stat-icon">
                <v-icon color="white">mdi-archive</v-icon>
              </div>
              <div class="stat-content">
                <div class="stat-value">40</div>
                <div class="stat-label">Total Bins</div>
              </div>
            </div>
          </v-col>
        </v-row>

        <!-- Legend -->
        <div class="legend">
          <div class="legend-item">
            <div class="legend-color available-color"></div>
            <span>Available</span>
          </div>
          <div class="legend-item">
            <div class="legend-color taken-color"></div>
            <span>Taken</span>
          </div>
        </div>

        <v-row>
          <!-- Bins Grid -->
          <v-col cols="12" md="8">
            <div class="bins-grid">
              <div
                v-for="binNumber in 40"
                :key="binNumber"
                class="bin-box"
                :class="{
                  'bin-available': isBinAvailable(binNumber),
                  'bin-taken': isBinTaken(binNumber),
                  'bin-selected': selectedBin === binNumber
                }"
                @click="selectBin(binNumber)"
              >
                <div class="bin-number">{{ binNumber }}</div>
                <div class="bin-status-icon">
                  <v-icon v-if="isBinAvailable(binNumber)" small color="white">
                    mdi-check
                  </v-icon>
                  <v-icon v-else small color="white">
                    mdi-close
                  </v-icon>
                </div>
              </div>
            </div>
          </v-col>

          <!-- Sample Preview Panel -->
          <v-col cols="12" md="4">
            <div class="preview-panel">
              <div v-if="!selectedBin" class="preview-empty">
                <v-icon size="64" color="#cbd5e0">mdi-information-outline</v-icon>
                <p class="empty-text">Click on a bin to view sample details</p>
              </div>

              <div v-else-if="!selectedSample" class="preview-empty">
                <v-icon size="64" color="#48bb78">mdi-check-circle</v-icon>
                <p class="empty-text">Bin {{ selectedBin }} is available</p>
              </div>

              <div v-else class="preview-content">
                <div class="preview-header">
                  <v-icon left color="#4a5568">mdi-package-variant</v-icon>
                  <span class="preview-title">Bin {{ selectedBin }}</span>
                </div>

                <div class="preview-fields">
                  <div class="preview-row">
                    <div class="preview-field-inline">
                      <div class="preview-label">Sample ID</div>
                      <div class="preview-value">{{ selectedSample.sampleId }}</div>
                    </div>
                    <div class="preview-field-inline">
                      <div class="preview-label">Status</div>
                      <div class="preview-value">
                        <v-chip 
                          x-small 
                          :color="getStatusColor(selectedSample.sampleStatus)"
                          dark
                        >
                          {{ selectedSample.sampleStatus }}
                        </v-chip>
                      </div>
                    </div>
                  </div>

                  <div class="preview-field">
                    <div class="preview-label">Customer</div>
                    <div class="preview-value">{{ selectedSample.customerName }}</div>
                  </div>

                  <div class="preview-field">
                    <div class="preview-label">Contact Person</div>
                    <div class="preview-value">{{ selectedSample.contactPerson }}</div>
                  </div>

                  <div class="preview-field">
                    <div class="preview-label">Reference #</div>
                    <div class="preview-value">{{ selectedSample.referenceNumber }}</div>
                  </div>

                  <div class="preview-row">
                    <div class="preview-field-inline">
                      <div class="preview-label">Date Received</div>
                      <div class="preview-value">{{ selectedSample.createdDate }}</div>
                    </div>
                    <div class="preview-field-inline" v-if="selectedSample.photoCount > 0">
                      <div class="preview-label">Photos</div>
                      <div class="preview-value">
                        <v-chip x-small color="#3b82f6" dark>
                          <v-icon left x-small>mdi-camera</v-icon>
                          {{ selectedSample.photoCount }}
                        </v-chip>
                      </div>
                    </div>
                  </div>

                  <div class="preview-field" v-if="selectedSample.receivedBy">
                    <div class="preview-label">Received By</div>
                    <div class="preview-value">{{ selectedSample.receivedBy }}</div>
                  </div>

                  <div class="preview-field" v-if="selectedSample.assignedTo">
                    <div class="preview-label">Assigned To</div>
                    <div class="preview-value">{{ selectedSample.assignedTo }}</div>
                  </div>

                  <div class="preview-field" v-if="selectedSample.sampleInformation">
                    <div class="preview-label">Sample Information</div>
                    <div class="preview-value info-text">{{ selectedSample.sampleInformation }}</div>
                  </div>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn
          color="#4a5568"
          dark
          @click="closeDialog"
          style="text-transform: none; border-radius: 8px;"
        >
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "SampleBinsDialog",
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    availableBins: {
      type: Array,
      default: () => []
    },
    takenBins: {
      type: Array,
      default: () => []
    },
    samples: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      selectedBin: null,
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit('update:visible', value);
      }
    },
    selectedSample() {
      if (!this.selectedBin) return null;
      
      // Find sample with this bin number
      const sample = this.samples.find(s => 
        s.binNumber && 
        s.binNumber.toString() === this.selectedBin.toString() &&
        s.sampleStatus !== 'Returned'
      );
      
      return sample || null;
    }
  },
  methods: {
    closeDialog() {
      this.selectedBin = null;
      this.dialogVisible = false;
    },
    isBinAvailable(binNumber) {
      return this.availableBins.some(bin => bin.number === binNumber);
    },
    isBinTaken(binNumber) {
      return this.takenBins.some(bin => bin.number === binNumber);
    },
    selectBin(binNumber) {
      this.selectedBin = binNumber;
    },
    getStatusColor(status) {
      const colorMap = {
        'Inspection': '#805ad5',
        'Drawing': '#d69e2e',
        'Quote': '#48bb78',
        'Returned': '#718096'
      };
      return colorMap[status] || '#4a5568';
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700&display=swap');

* {
  font-family: 'Montserrat', sans-serif;
}

.dialog-title {
  font-size: 1.25rem;
  font-weight: 700;
}

/* Stats Cards */
.stat-card {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  gap: 12px;
}

.available-stat {
  background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
  color: white;
}

.taken-stat {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
}

.total-stat {
  background: linear-gradient(135deg, #4a5568 0%, #374151 100%);
  color: white;
}

.stat-icon {
  flex-shrink: 0;
}

.stat-content {
  flex: 1;
}

.stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 0.75rem;
  opacity: 0.9;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Legend */
.legend {
  display: flex;
  gap: 24px;
  justify-content: center;
  padding: 12px;
  background: #f7fafc;
  border-radius: 8px;
  margin-bottom: 16px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 0.875rem;
}

.legend-color {
  width: 24px;
  height: 24px;
  border-radius: 4px;
}

.available-color {
  background: #48bb78;
}

.taken-color {
  background: #ef4444;
}

/* Bins Grid */
.bins-grid {
  display: grid;
  grid-template-columns: repeat(10, 1fr);
  gap: 8px;
  margin-bottom: 20px;
}

.bin-box {
  aspect-ratio: 1;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-weight: 700;
  transition: all 0.3s ease;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.bin-box:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.bin-available {
  background: #48bb78;
  color: white;
}

.bin-taken {
  background: #ef4444;
  color: white;
}

.bin-selected {
  box-shadow: 0 0 0 3px #4a5568;
  transform: scale(1.05);
}

.bin-number {
  font-size: 1rem;
  line-height: 1;
}

.bin-status-icon {
  line-height: 1;
}

/* Preview Panel */
.preview-panel {
  background: #f7fafc;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  height: 100%;
  max-height: 350px;
  padding: 12px;
  overflow-y: auto;
}

.preview-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 400px;
}

.empty-text {
  margin-top: 16px;
  color: #718096;
  font-size: 0.875rem;
  text-align: center;
}

.preview-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.preview-header {
  display: flex;
  align-items: center;
  padding-bottom: 12px;
  border-bottom: 2px solid #e2e8f0;
}

.preview-title {
  font-size: 1.125rem;
  font-weight: 700;
  color: #2d3748;
}

.preview-fields {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.preview-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.preview-field,
.preview-field-inline {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.preview-label {
  font-size: 0.7rem;
  font-weight: 700;
  color: #4a5568;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.preview-value {
  font-size: 0.8rem;
  color: #2d3748;
  font-weight: 500;
}

.info-text {
  background: white;
  padding: 10px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 120px;
  overflow-y: auto;
  font-size: 0.75rem;
  line-height: 1.4;
}

/* Responsive */
@media (max-width: 1200px) {
  .bins-grid {
    grid-template-columns: repeat(8, 1fr);
  }
}

@media (max-width: 960px) {
  .bins-grid {
    grid-template-columns: repeat(10, 1fr);
  }
  
  .preview-panel {
    min-height: 300px;
  }
  
  .preview-empty {
    min-height: 250px;
  }
}

@media (max-width: 768px) {
  .bins-grid {
    grid-template-columns: repeat(8, 1fr);
  }
  
  .bin-number {
    font-size: 0.875rem;
  }
  
  .stat-card {
    padding: 16px;
  }
  
  .stat-value {
    font-size: 1.5rem;
  }
}
</style>